const DEFAULT_MESSAGE = "Something went wrong. Please try again.";

type ApiErrorShape = {
	response?: { status?: number; data?: { message?: string | string[] } };
	message?: string;
};

/**
 * Extracts a user-facing message from anything that can be thrown or rejected.
 *
 * Not every failure is an Axios error. `fetch` rejects with a bare `TypeError`
 * that has no `response`, and Nest returns `message` as an array for validation
 * errors. Reading `error.response.data.message` directly throws a second error
 * inside the handler, which leaves the UI stuck in a loading state.
 */
export function getErrorMessage(error: unknown, fallback: string = DEFAULT_MESSAGE): string {
	if (!error) return fallback;

	if (typeof error === "string") return error.trim() || fallback;

	const apiMessage = (error as ApiErrorShape)?.response?.data?.message;

	if (Array.isArray(apiMessage)) {
		const joined = apiMessage.filter(Boolean).join(" ");
		if (joined) return joined;
	}

	if (typeof apiMessage === "string" && apiMessage.trim()) return apiMessage;

	const nativeMessage = (error as ApiErrorShape)?.message;
	if (typeof nativeMessage === "string" && nativeMessage.trim()) return nativeMessage;

	return fallback;
}

/** True when the API rejected the request because the file hash is already in use. */
export function isConflictError(error: unknown): boolean {
	return (error as ApiErrorShape)?.response?.status === 409;
}
